// hooks/useFetchLocationsAndBrands.ts
import { useCallback } from "react";
import { getAppStoreUrl } from "../../web-redemption/PaymentMethod";
import { clearMarkers } from "../utils/markers";

interface FetchOptions {
  brandSlug?: string;
  queryParams: Record<string, any>;
  showDeliveryOnly: boolean;
  hydrateLocationsAndBrands: (data: any) => void;
  markersState: google.maps.Marker[];
  setMarkersState: (markers: google.maps.Marker[]) => void;
}

/**
 * Custom hook for fetching locations and brands data,
 * clearing existing map markers, and hydrating fresh data into state.
 *
 * @returns fetchLocationsAndBrands - Call this to trigger the fetch-and-hydrate flow.
 */
export function useFetchLocationsAndBrands({
  brandSlug,
  queryParams,
  showDeliveryOnly,
  hydrateLocationsAndBrands,
  markersState,
  setMarkersState,
}: FetchOptions) {
  const fetchLocationsAndBrands = useCallback(async () => {
    try {
      // Build base URL depending on environment
      const baseUrl =
        queryParams.env === "prod"
          ? "https://app.inkind.com/"
          : getAppStoreUrl();
      const versionUrl =
        queryParams.ver === "6" ? "api/v6/map/map" : "api/v5/map";

      // Build dynamic URL parameters
      const params = new URLSearchParams();
      if (queryParams.tag) params.append("tag", queryParams.tag);

      const finalUrl =
        baseUrl +
        versionUrl +
        (params.toString() ? `?${params.toString()}` : "");

      const response = await fetch(finalUrl);
      const data = await response.json();

      // Step 1: Clear old markers from map
      clearMarkers(markersState, setMarkersState);

      // Step 2: Hydrate fresh locations and brands into state
      hydrateLocationsAndBrands(data);
    } catch (error) {
      console.error("Error fetching locations and brands:", error);
    }
  }, [
    brandSlug,
    queryParams,
    showDeliveryOnly,
    hydrateLocationsAndBrands,
    markersState,
    setMarkersState,
  ]);

  return fetchLocationsAndBrands;
}
